import { useRef, useState, useEffect } from 'react';
import { useSpring } from 'react-spring';
import { on, off } from '../../../utils/dom.js';
import { Esc } from '../../../utils/keycode.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
const useDrawer = ({
  escToExit,
  width,
  height,
  visible,
  placement,
  onCancel
}) => {
  const drawerWrapperRef = useRef(null);
  const contentWrapperRef = useRef(null);
  const [popupZIndex, setPopupZIndex] = useState();
  // Record whether is exiting, to prevent `onCancel` being unnecessarily triggered when mask is clicked during the period.
  const inExit = useRef(false);
  // Record whether it's opened to avoid element shaking during animation caused by focus lock.

  // Overlay
  const overlayStyle = useSpring({
    opacity: !visible ? '0' : '1',
    reset: true,
    delay: 50
  });
  const getPlacement = placement => {
    switch (placement) {
      case 'left':
        return `translate(-${width}px, 0px)`;
      case 'right':
        return `translate(${width}px, 0px)`;
      case 'top':
        return `translate(0px, -${height}px)`;
      default:
        return `translate(0px, ${height}px)`;
    }
  };
  const springConfig = {
    from: {
      transform: getPlacement(placement),
      config: {
        duration: 200
      }
    },
    to: {
      transform: 'translate(0px, 0px)',
      config: {
        duration: 200
      }
    }
  };
  useEffect(() => {
    if (visible && popupZIndex === undefined && drawerWrapperRef.current) {
      // Set zIndex for nested drawer components based on zIndex of wrapper
      const zIndex = Number(window.getComputedStyle(drawerWrapperRef.current, null)?.zIndex);
      if (!Number.isNaN(zIndex)) {
        setPopupZIndex(zIndex + 1);
      }
    }
  }, [visible, popupZIndex]);
  useEffect(() => {
    const onKeyDown = e => {
      if (escToExit && e && e.code === Esc.key && onCancel) {
        onCancel(e);
      }
    };
    if (visible) {
      on(document, 'keydown', onKeyDown);
    }
    return () => {
      off(document, 'keydown', onKeyDown);
    };
  }, [visible, escToExit, onCancel]);
  return {
    contentWrapperRef,
    springConfig,
    overlayStyle,
    drawerWrapperRef,
    inExit
  };
};

export { useDrawer };
